<template>
    <div>
        <div class="tw-grid tw-auto-rows-max tw-gap-16 tw-grid-cols-2 tw-mt-8">
            <div class="tw-col-span-1">
                <l-multi-filter
                    saved-view-type="checkinAnalytics"
                    :filter-types="filterTypes"
                    :show-custom-attributes="true"
                    :preselected-filters="selectedFilters"
                    :auto-apply-preselected-filters="true"
                    @update="applyFilters"
                />
            </div>

            <l-text-alert
                v-if="hasLoadingError"
                type="danger"
                :text="$t('meetings.analytics.loadingError')"
                class="tw-col-span-2"
            />

            <div class="tw-col-span-2">
                <l-content-box>
                    <l-skeleton-loader v-if="loading.analytics" />
                    <l-meeting-analytics-totals v-else :analytics="analytics" />
                </l-content-box>
            </div>

            <div
                v-if="!loading.analytics && analytics.meetingTotals.amountTotal > 0"
                class="tw-col-span-2"
            >
                <l-content-box>
                    <template #headline>
                        {{ $t("meetings.analytics.insights.title") }}
                    </template>
                    <l-meeting-analytics-distribution
                        :analytics="analytics"
                        :time-unit="timeUnitInChart"
                    />
                </l-content-box>
            </div>

            <div
                class="tw-col-span-2 tw-border-0 tw-border-t tw-border-gray-100 tw-border-solid tw-pt-16"
            >
                <l-content-box>
                    <template #headline>
                        {{ $t("meetings.analytics.userlist.title") }}
                        <span
                            v-if="Array.isArray(userList) && userList.length && !loading.userList"
                            class="tw-text-sm tw-leading-7 tw-font-bold tw-text-gray-400"
                        >
                            ( {{ $t("meetings.analytics.labels.top") }} {{ userList.length }} )
                        </span>
                    </template>

                    <l-skeleton-loader v-if="loading.userList" />
                    <l-meeting-analytics-user-list
                        v-else
                        :items="userList"
                        :is-loading-details="loading.userDetails"
                        :time-unit="timeUnitInChart"
                        @loadUserDetails="onLoadUserDetails"
                    />
                </l-content-box>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LMultiFilter from "@/components/LMultiFilter";
import LMeetingAnalyticsUserList from "@/components/LMeetingAnalytics/LMeetingAnalyticsUserList";
import LMeetingAnalyticsTotals from "@/components/LMeetingAnalytics/LMeetingAnalyticsTotals";
import LMeetingAnalyticsDistribution from "@/components/LMeetingAnalytics/LMeetingAnalyticsDistribution";

import LContentBox from "@/components/LContentBox";
import LSkeletonLoader from "@/components/LSkeletonLoader";
import LTextAlert from "@/components/LTextAlert";

export default {
    components: {
        LMultiFilter,
        LMeetingAnalyticsUserList,
        LMeetingAnalyticsTotals,
        LMeetingAnalyticsDistribution,
        LContentBox,
        LSkeletonLoader,
        LTextAlert,
    },
    data() {
        return {
            loading: {
                userList: false,
                userDetails: false,
                analytics: false,
            },
            hasLoadingError: false,
            filters: {},
            selectedFilters: {},
        };
    },
    computed: {
        ...mapGetters({
            userList: "meetings/stats/userList",
            userDetails: "meetings/stats/userDetails",
            analytics: "meetings/stats/analytics",
        }),
        timeUnitInChart() {
            let unit = "month";
            let dateSelected = this.$route.query.date || "";

            if (dateSelected.startsWith("m_")) {
                unit = "day";
            } else if (dateSelected.startsWith("tp_")) {
                unit = "week";
            }

            return unit;
        },
        filterTypes() {
            return [
                {
                    value: "localTeams",
                    key: "localTeams",
                    text: this.$t(`multiFilter.selectLocalTeams`),
                },
                {
                    value: "managers",
                    key: "managers",
                    text: this.$t(`multiFilter.selectManagers`),
                },
                {
                    value: "users",
                    key: "users",
                    text: this.$t(`multiFilter.selectUsers`),
                },
                {
                    value: "locations",
                    key: "locations",
                    text: this.$t(`multiFilter.selectLocations`),
                },
                {
                    value: "periods",
                    key: "periods",
                    text: this.$t(`multiFilter.selectTimeframe`),
                },
                {
                    value: "admin",
                    key: "adminFilter",
                    text: this.$t("multiFilter.selectAdmin"),
                    options: [
                        {
                            _id: "indirectReportsSearch",
                            name: this.$t(`multiFilter.optionIndirectReports`),
                        },
                    ],
                },
            ];
        },
    },
    created() {
        this.selectedFilters = { admin: ["indirectReportsSearch"] };
        this.loadData();
    },
    methods: {
        applyFilters(filters) {
            this.filters = filters;
            this.loadData();
        },
        async onLoadUserDetails(id) {
            if (this.userDetails[id]) return;

            this.loading.userDetails = true;

            let periods;

            if (!this.filters?.periods?.[0]?.start && !this.filters?.periods?.[0]?.end) {
                periods = [{ start: this.$moment().subtract(6, "months"), end: this.$moment() }];
            } else {
                periods = this.filters.periods;
            }

            try {
                await this.$store.dispatch({
                    type: "meetings/stats/getUserDetails",
                    payload: { query: { filters: { users: [{ _id: id }], periods } } },
                });
            } catch {
                // API is likely down. We prefer to not show error messages here as the action is
                // triggered on item hover and only expands the available data with additional details
            } finally {
                this.loading.userDetails = false;
            }
        },
        async loadUserlist() {
            this.loading.userList = true;

            try {
                await this.$store.dispatch({
                    type: "meetings/stats/getUserlist",
                    payload: { query: { filters: this.filters } },
                });
            } catch {
                this.hasLoadingError = true;
            } finally {
                this.loading.userList = false;
            }
        },
        async loadAnalytics() {
            this.loading.analytics = true;

            try {
                await this.$store.dispatch({
                    type: "meetings/stats/getAnalytics",
                    payload: { query: { filters: this.filters } },
                });
            } catch {
                this.hasLoadingError = true;
            } finally {
                this.loading.analytics = false;
            }
        },
        async loadData() {
            if (!this.filters?.periods?.[0]?.start && !this.filters?.periods?.[0]?.end) {
                this.filters.periods = [
                    { start: this.$moment().subtract(6, "months"), end: this.$moment() },
                ];
            }

            await Promise.all([this.loadUserlist(), this.loadAnalytics()]);
        },
    },
};
</script>
